import React, { useEffect, useRef } from 'react';
import { TinsightParam } from '../../shared/types/computedKeyParam.type';
import { IStrideFreq } from '../../shared/types/Stabledata.type';
import { Box, Container, Typography } from '@mui/material';
import { t } from 'i18next';
import palette from '../../theme/palette';
import { inRange, isEmpty, max, min, orderBy } from 'lodash';
import HighchartsReact, { HighchartsReactRefObject } from 'highcharts-react-official';
import Highcharts from 'highcharts';

const VMAX_KEY = 'insight.stride_frequency_at_vmax';
const STRIDE_KEY = 'insight.stride_frequency_at_60';

function StrideFrequencyPage({ data, insightParams }: { data: IStrideFreq; insightParams: TinsightParam[] }) {
    const [targetX, setTargetX] = React.useState<number | null>(null);
    const [barGradient, setBarGradient] = React.useState<{
        gradient: string;
        width: number;
        left: number;
        top: number;
        startX?: number | null;
        endX?: number | null;
    } | null>(null);

    const VMAX_COLOR = '#000080';
    const chartRef = useRef<HighchartsReactRefObject | null>(null);



    const sortedData = orderBy(
        data.horse_averages,
        item => item.averages.find(avg => avg.parameter_name === STRIDE_KEY)?.raw_numeric_value || 0,
        'desc'
    );

    const getHorses = () => {
        if (isEmpty(data)) return [];
        return sortedData.map(horse => horse.horse.name || '');
    };

    const getBarsData = () => {
        if (isEmpty(data)) return [];
        return sortedData.map(e => {
            const value = e.averages.find(avg => avg.parameter_name === STRIDE_KEY);
            return { y: value?.raw_numeric_value || 0, color: value?.color };
        });
    };



    const getLineData = () => {
        if (isEmpty(data)) return [];
        return sortedData.map(e => e.averages.find(avg => avg.parameter_name === VMAX_KEY)?.raw_numeric_value || 0);
    };

    const getStrideTitle = () => t(insightParams.find(param => param.name === STRIDE_KEY)?.name_translated || '').concat(` (${t('firstSection:average')})`);
    const getVMAXTitle = () => t(insightParams.find(param => param.name === VMAX_KEY)?.name_translated || '').concat(` (${t('firstSection:average')})`);
    const computeBarGradient = () => {
        const chart = chartRef.current?.chart;
        if (!chart) return;

        const points = chart.series[0].points;
        if (!points.length) return;

        const first = points[0];
        const last = points[points.length - 1];

        const totalStart = first.shapeArgs?.x ?? 0;
        const totalEnd = (last.shapeArgs?.x ?? 0) + (last.shapeArgs?.width ?? 0);
        const totalWidth = totalEnd - totalStart;

        // 1. Build Gradient
        const stops: string[] = points.map(p => {
            const start = ((p.shapeArgs?.x ?? 0) - totalStart) / totalWidth * 100;
            const end = start + ((p.shapeArgs?.width ?? 0) / totalWidth * 100);
            return `${p.color} ${start.toFixed(2)}%, ${p.color} ${end.toFixed(2)}%`;
        });
        const gradient = `linear-gradient(to right, ${stops.join(', ')})`;

        const plotTop = chart.plotTop;
        const barStart = chart.plotLeft + totalStart;
        const barEnd = chart.plotLeft + totalEnd;

        // 2. Find target value position
        const targetValue = 2.42;
        let foundX: number | null = null;

        const targetPoint = points.find(p => Number(((p?.y || 0) / 50).toFixed(2)) === targetValue);

        if (targetPoint) {
            foundX = chart.plotLeft + (targetPoint.shapeArgs?.x ?? 0) + (targetPoint.shapeArgs?.width ?? 2) / 2;
        } else {
            let yPoints = points.map(point => Number(point.y) / 50);
            const maxVal = max(yPoints) || 0;
            const minVal = min(yPoints) || 0;

            if (inRange(targetValue, minVal, maxVal)) {
                const sortedPoints = [...points].sort((a, b) => (a.y || 0) - (b.y || 0));
                for (let i = 0; i < sortedPoints.length - 1; i++) {
                    const p1 = sortedPoints[i];
                    const p2 = sortedPoints[i + 1];
                    const v1 = Number(((p1.y || 0) / 50).toFixed(2));
                    const v2 = Number(((p2.y || 0) / 50).toFixed(2));

                    if (inRange(targetValue, v1, v2)) {
                        const range = v2 - v1;
                        const percentage = range === 0 ? 0 : (targetValue - v1) / range;
                        const x1 = chart.plotLeft + (p1.shapeArgs?.x ?? 0) + (p1.shapeArgs?.width ?? 0) / 2;
                        const x2 = chart.plotLeft + (p2.shapeArgs?.x ?? 0) + (p2.shapeArgs?.width ?? 0) / 2;
                        foundX = x1 + (x2 - x1) * percentage;
                        break;
                    }
                }
            } else {
                // If data is sorted 'desc' (2.5 -> 2.3), then 2.42 is physically to the RIGHT 
                // of a 2.5 Sprinter but to the LEFT of a 2.38 Stayer.
                if (targetValue < minVal) {
                    // Target is smaller than the smallest (Stayer side) -> Move to right
                    foundX = barEnd + 20;
                } else if (targetValue > maxVal) {
                    // Target is bigger than the biggest (Sprinter side) -> Move to left
                    foundX = barStart - 20;
                }
            }
        }

        const safePadding = 0;

        if (foundX) {
            // Ensure Miller stays to the right of the Sprinter zone
            if (foundX < barStart + safePadding) {
                foundX = barStart + safePadding;
            }
            // Ensure Miller stays to the left of the Stayer zone
            if (foundX as number > barEnd - safePadding) {
                foundX = barEnd - safePadding;
            }
        } else {
            foundX = barStart + (totalWidth / 2);
        }
        setTargetX(foundX);

        // 4. Set Labels with extra margin for the Sprinter
        setBarGradient({
            gradient,
            width: totalWidth,
            left: barStart,
            top: plotTop - 20,
            // Shifted further left (-50) to create that "empty" start area
            startX: barStart - 30,
            // Kept at +20 or match symmetry with +50
            endX: barEnd + 25
        });
    };

    useEffect(() => { computeBarGradient(); }, [chartRef.current]);

    const options: Highcharts.Options = {
        chart: {
            height: 650,
            animation: false,
            spacingTop: 40,
            style: { overflow: 'visible' },
            backgroundColor: 'transparent',
            events: { load: () => computeBarGradient() }
        },
        title: { text: t('stable:title:strideChartTitle'), align: 'left', y: 10, margin: 50, style: { fontSize: "14px" } },
        plotOptions: {
            series: {
                animation: false,
                states: { hover: { enabled: false }, inactive: { enabled: false, opacity: 1 } }, marker: { symbol: 'circle' }
            },
            spline: {
                dataLabels: {
                    enabled: true,
                    useHTML: true,
                    formatter: function () {
                        return `<div style="background: #4169E1;border-radius: 4px;padding: 1px;color:#fff;font-size: 9px;box-shadow: 0 1px 3px #4169E1;white-space: nowrap;
                        opacity: 1 !important; /* Forces opacity */
                        z-index: 9999 !important;
                        ">${(Number(this.y) / 50).toFixed(2)}</div>`;
                    },
                    align: 'center',
                    verticalAlign: 'middle',
                    y: -20
                },
                marker: { enabled: true, radius: 4 }
            },
            column: {
                pointPadding: 0.1,
                groupPadding: 0.1,
                borderWidth: 0,
                dataLabels: {
                    enabled: true,
                    useHTML: true,
                    inside: true,
                    verticalAlign: 'middle',
                    align: 'center',
                    formatter: function () {
                        return `<div style="background: ${VMAX_COLOR};border-radius: 4px;padding: 1px;color:#fff;font-size: 9px;box-shadow: 0 1px 3px #4169E1;white-space: nowrap;
                        opacity: 1 !important; /* Forces opacity */
                            z-index: 9999 !important;
                        ">${(Number(this.y) / 50).toFixed(2)}</div>`;
                    }
                }
            }
        },
        credits: { enabled: false },
        xAxis: {
            categories: getHorses(),
            minPadding: 0.05,
            maxPadding: 0.05,
            gridLineWidth: 0,
            endOnTick: false,
            showLastLabel: true,
            labels: { allowOverlap: true, useHTML: true, rotation: -40, style: { fontSize: '11px' }, step: 1 }
        },
        legend: { enabled: true, layout: 'horizontal', align: 'right', verticalAlign: 'top', x: -90, y: -10, floating: true, itemMarginTop: 5, itemMarginBottom: 5, padding: 8, symbolPadding: 8 },
        yAxis: [
            { min: 100, startOnTick: false, endOnTick: false, tickInterval: 5, gridLineWidth: 0, title: { text: getStrideTitle() }, labels: { formatter: function () { return (Number(this.value) / 50).toString(); } } },
            { min: 100, startOnTick: false, endOnTick: false, tickInterval: 5, gridLineWidth: 0, title: { text: getVMAXTitle() }, labels: { formatter: function () { return (Number(this.value) / 50).toString(); } }, opposite: true }
        ],
        tooltip: { enabled: false },
        series: [
            { name: getStrideTitle(), data: getBarsData(), type: 'column', zIndex: 0, yAxis: 0, pointPlacement: 0.05 },
            { name: getVMAXTitle(), data: getLineData(), type: 'spline', color: VMAX_COLOR, zIndex: 2, yAxis: 1 }
        ]
    };

    return (
        <>
            <Box
                sx={{ width: '100%' }}
                display="flex" justifyContent="space-between" alignItems="center" gap={3} mb={3}>
                <Typography variant="h4" sx={{
                    color: palette.light.primary.main, alignSelf: 'self-start', flex: 1,
                    whiteSpace: 'nowrap',
                    overflow: 'visible',
                    textOverflow: 'clip'
                }}>
                    {t('graph:cadence')}
                </Typography>
                <Box sx={{ p: 1, border: `1px solid ${palette.light.primary.main}`, borderRadius: 2 }}>
                    <Typography sx={{ lineHeight: '1.1' }} variant='caption'>{t('stable:title:strideHint')}</Typography>
                </Box>
                <img style={{ objectFit: 'contain', width: 55, height: 55, marginRight: 15, alignSelf: 'self-start' }} src="/arioneo_logo_white.png" alt="arioneo logo" />
            </Box >
            <div style={{ width: '1200px', position: 'relative' }} id="chart-container">
                {barGradient && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: barGradient.top - 15,
                            left: barGradient.left,
                            width: barGradient.width,
                            height: 15,
                            borderRadius: 2,
                            background: barGradient.gradient,
                            zIndex: 2,
                        }}
                    />
                )}

                {/* Target value */}
                {barGradient && targetX && (
                    <Box sx={{ position: 'absolute', top: barGradient.top, left: targetX, fontSize: 11, transform: 'translateX(-50%)', fontWeight: 500, color: '#000', whiteSpace: 'nowrap', zIndex: 3 }}>
                        Miler
                    </Box>
                )}

                {/* Start label */}
                {barGradient?.startX && (
                    <Box sx={{ position: 'absolute', top: barGradient.top - 15, left: barGradient.startX, transform: 'translateX(-50%)', fontSize: 11, fontWeight: 500, color: '#000', whiteSpace: 'nowrap', zIndex: 3 }}>
                        Sprinter
                    </Box>
                )}

                {/* End label */}
                {barGradient?.endX && (
                    <Box sx={{ position: 'absolute', top: barGradient.top - 15, left: barGradient.endX, transform: 'translateX(-50%)', fontSize: 11, fontWeight: 500, color: '#000', whiteSpace: 'nowrap', zIndex: 3 }}>
                        Stayer
                    </Box>
                )}

                <HighchartsReact ref={chartRef} highcharts={Highcharts} options={options} />
            </div>
        </>
    );
}

export default StrideFrequencyPage;
